import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { ReconnectBanner } from "./ReconnectBanner";
import {
	useCanvaQuery,
	useFacebook,
	useGoogleMyBusiness,
	useInstagram,
	useLinkedin,
	useMetaAds,
	useThreads,
	useTikTok,
	useTikTokAds,
	useWordpress,
	useX,
	useYouTube,
} from "./useIntegrations";

type ExpiredConnection = {
	name: string;
	title: string;
	description: string;
	actionLabel: string;
};

const joinNames = (names: string[]) => {
	if (names.length === 2) {
		return `${names[0]} and ${names[1]}`;
	}

	return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;
};

export const ExpiredConnectionsBanner = () => {
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isXEnabled = useFeatureFlag("XIntegration");
	const isGoogleBusinessEnabled = useFeatureFlag("GoogleMyBusinessIntegration");
	const isCanvaEnabled = useFeatureFlag("CanvaIntegration");
	const isMetaAdsEnabled = useFeatureFlag("MetaAdsIntegration");
	const isTikTokAdsEnabled = useFeatureFlag("TikTokAdsIntegration");

	const { status: facebook } = useFacebook();
	const { status: instagram } = useInstagram();
	const { status: threads } = useThreads();
	const { status: linkedin } = useLinkedin();
	const { status: youtube } = useYouTube();
	const { status: tiktok } = useTikTok();
	const xStatus = useX();
	const { status: googleBusiness } = useGoogleMyBusiness();
	const { status: wordpress } = useWordpress();
	const { status: metaAds } = useMetaAds();
	const { status: tiktokAds } = useTikTokAds();
	const { data: canvaStatus } = useCanvaQuery();

	const tiktokAdsNeedsReconnect =
		tiktokAds.data.status === "requires_reconnection" ||
		(!tiktokAds.data.sandboxMode &&
			tiktokAds.data.businessAccount?.tokenExpired === true);

	const expired: ExpiredConnection[] = [
		facebook.data.tokenExpired && {
			name: "Facebook",
			title: "Your Facebook connection needs to be renewed",
			description: "Scheduled Facebook posts will fail until you reconnect.",
			actionLabel: "Reconnect Facebook",
		},
		instagram.data.tokenExpired && {
			name: "Instagram",
			title: "Your Instagram connection needs to be renewed",
			description: "Scheduled Instagram posts will fail until you reconnect.",
			actionLabel: "Reconnect Instagram",
		},
		threads.data.tokenExpired && {
			name: "Threads",
			title: "Your Threads connection needs to be renewed",
			description: "Scheduled Threads posts will fail until you reconnect.",
			actionLabel: "Reconnect Threads",
		},
		linkedin.data.tokenExpired && {
			name: "LinkedIn",
			title: "Your LinkedIn connection needs to be renewed",
			description: "Scheduled LinkedIn posts will fail until you reconnect.",
			actionLabel: "Reconnect LinkedIn",
		},
		isYouTubeEnabled &&
			youtube.data.tokenExpired && {
				name: "YouTube",
				title: "Your YouTube connection needs to be renewed",
				description: "Scheduled Shorts are paused until you reconnect.",
				actionLabel: "Reconnect YouTube",
			},
		isTikTokEnabled &&
			tiktok.data.tokenExpired && {
				name: "TikTok",
				title: "Your TikTok connection needs to be renewed",
				description: "Scheduled TikTok posts will fail until you reconnect.",
				actionLabel: "Reconnect TikTok",
			},
		isXEnabled &&
			xStatus.tokenExpired && {
				name: "X",
				title: "Your X connection needs to be renewed",
				description: "Scheduled X posts will fail until you reconnect.",
				actionLabel: "Reconnect X",
			},
		isGoogleBusinessEnabled &&
			googleBusiness.data.tokenExpired && {
				name: "Google Business",
				title: "Your Google Business connection needs to be renewed",
				description:
					"Scheduled Google Business posts will fail until you reconnect.",
				actionLabel: "Reconnect Google Business",
			},
		wordpress.data.tokenExpired && {
			name: "WordPress",
			title: "Your WordPress connection needs to be renewed",
			description:
				"Blog publishing to WordPress will fail until you reconnect.",
			actionLabel: "Reconnect WordPress",
		},
		isCanvaEnabled &&
			canvaStatus?.connected &&
			canvaStatus.tokenExpired && {
				name: "Canva",
				title: "Your Canva connection has expired",
				description: "Reconnect to browse and import designs.",
				actionLabel: "Reconnect Canva",
			},
		isMetaAdsEnabled &&
			metaAds.data.tokenExpired && {
				name: "Meta Ads",
				title: "Your Meta Ads connection needs to be renewed",
				description: "Campaign management is paused until you reconnect.",
				actionLabel: "Reconnect Meta Ads",
			},
		isTikTokAdsEnabled &&
			tiktokAdsNeedsReconnect && {
				name: "TikTok Ads",
				title: "Your TikTok Ads connection needs to be renewed",
				description: "Campaign management is paused until you reconnect.",
				actionLabel: "Reconnect TikTok Ads",
			},
	].filter((connection): connection is ExpiredConnection =>
		Boolean(connection),
	);

	if (expired.length === 0) {
		return null;
	}

	if (expired.length === 1) {
		const [connection] = expired;
		if (!connection) {
			return null;
		}

		return (
			<ReconnectBanner
				title={connection.title}
				description={connection.description}
				actionLabel={connection.actionLabel}
				to={LinkGen.Settings.Apps()}
			/>
		);
	}

	const names = expired.map((connection) => connection.name);

	return (
		<ReconnectBanner
			title={`${expired.length} platforms need to be reconnected`}
			description={`${joinNames(names)}. Scheduled posts will fail until you reconnect.`}
			actionLabel="Reconnect"
			to={LinkGen.Settings.Apps()}
		/>
	);
};
